import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { 
  Eye, MousePointerClick, Phone, MessageCircle, 
  TrendingUp, Calendar, BarChart3, ArrowUpRight,
  ArrowDownRight, Clock, Users, DollarSign
} from "lucide-react";
import api from "@/utils/api";
import { toast } from "sonner";

const Analytics = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [ads, setAds] = useState([]);
  const [selectedAd, setSelectedAd] = useState(null);
  const [dateRange, setDateRange] = useState("7d");

  useEffect(() => {
    fetchAnalytics();
    fetchMyAds();
  }, [dateRange]);

  const fetchAnalytics = async () => {
    try {
      const response = await api.get(`/analytics/overview?range=${dateRange}`);
      setAnalytics(response.data);
    } catch (error) {
      console.error("Failed to fetch analytics", error);
    }
  };

  const fetchMyAds = async () => {
    try {
      const response = await api.get("/my-ads");
      setAds(response.data);
      if (response.data.length > 0 && !selectedAd) {
        setSelectedAd(response.data[0]);
      }
    } catch (error) {
      toast.error("Failed to fetch ads");
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + "M";
    if (num >= 1000) return (num / 1000).toFixed(1) + "K";
    return num?.toString() || "0";
  };

  const calculateCTR = (clicks, views) => {
    if (!views || views === 0) return "0%";
    return ((clicks / views) * 100).toFixed(2) + "%";
  };

  const StatCard = ({ title, value, icon: Icon, trend, trendValue, color = "rose" }) => (
    <div className="bg-slate-900/50 border border-white/10 rounded-xl p-6">
      <div className="flex items-start justify-between mb-4">
        <div className={`w-12 h-12 bg-${color}-500/20 rounded-lg flex items-center justify-center`}>
          <Icon className={`w-6 h-6 text-${color}-400`} />
        </div>
        {trend && (
          <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs ${
            trend === "up" ? "bg-emerald-500/20 text-emerald-400" : "bg-red-500/20 text-red-400"
          }`}>
            {trend === "up" ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
            {trendValue}
          </div>
        )}
      </div>
      <p className="text-gray-400 text-sm mb-1">{title}</p>
      <p className="text-2xl font-bold text-white">{value}</p>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950">
        <Header />
        <div className="flex items-center justify-center h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-rose-500"></div>
        </div>
      </div>
    );
  }

  const totalViews = ads.reduce((sum, ad) => sum + (ad.views || 0), 0);
  const totalWhatsappClicks = ads.reduce((sum, ad) => sum + (ad.whatsapp_clicks || 0), 0);
  const totalCallClicks = ads.reduce((sum, ad) => sum + (ad.call_clicks || 0), 0);
  const totalTelegramClicks = ads.reduce((sum, ad) => sum + (ad.telegram_clicks || 0), 0);
  const totalClicks = totalWhatsappClicks + totalCallClicks + totalTelegramClicks;

  return (
    <div className="min-h-screen bg-slate-950">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white">Analytics Dashboard</h1>
            <p className="text-gray-400 mt-1">Track your ad performance and engagement metrics</p>
          </div>
          
          {/* Date Range Selector */}
          <div className="flex gap-2">
            {[
              { value: "7d", label: "7 Days" },
              { value: "30d", label: "30 Days" },
              { value: "90d", label: "90 Days" }
            ].map((option) => (
              <button
                key={option.value}
                onClick={() => setDateRange(option.value)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  dateRange === option.value
                    ? "bg-rose-600 text-white"
                    : "bg-slate-800 text-gray-300 hover:bg-slate-700"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* Overview Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard 
            title="Total Views" 
            value={formatNumber(totalViews)} 
            icon={Eye}
            color="blue"
          />
          <StatCard 
            title="Total Clicks" 
            value={formatNumber(totalClicks)} 
            icon={MousePointerClick}
            color="emerald"
          />
          <StatCard 
            title="Click Rate (CTR)" 
            value={calculateCTR(totalClicks, totalViews)} 
            icon={TrendingUp}
            color="amber"
          />
          <StatCard 
            title="Active Ads" 
            value={ads.filter(ad => ad.status === "approved").length} 
            icon={BarChart3}
            color="purple"
          />
        </div>

        {/* Click Breakdown */}
        <div className="grid md:grid-cols-3 gap-4 mb-8">
          <div className="bg-slate-900/50 border border-white/10 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                <MessageCircle className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-gray-400 text-sm">WhatsApp Clicks</p>
                <p className="text-xl font-bold text-white">{formatNumber(totalWhatsappClicks)}</p>
              </div>
            </div>
            <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
              <div 
                className="h-full bg-emerald-500 rounded-full"
                style={{ width: `${totalClicks ? (totalWhatsappClicks / totalClicks) * 100 : 0}%` }}
              />
            </div>
            <p className="text-gray-500 text-xs mt-2">
              {totalClicks ? ((totalWhatsappClicks / totalClicks) * 100).toFixed(1) : 0}% of total clicks
            </p>
          </div>

          <div className="bg-slate-900/50 border border-white/10 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                <Phone className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-gray-400 text-sm">Call Clicks</p>
                <p className="text-xl font-bold text-white">{formatNumber(totalCallClicks)}</p>
              </div>
            </div>
            <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
              <div 
                className="h-full bg-blue-500 rounded-full"
                style={{ width: `${totalClicks ? (totalCallClicks / totalClicks) * 100 : 0}%` }}
              />
            </div>
            <p className="text-gray-500 text-xs mt-2">
              {totalClicks ? ((totalCallClicks / totalClicks) * 100).toFixed(1) : 0}% of total clicks
            </p>
          </div>

          <div className="bg-slate-900/50 border border-white/10 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-sky-500/20 rounded-lg flex items-center justify-center">
                <MessageCircle className="w-5 h-5 text-sky-400" />
              </div>
              <div>
                <p className="text-gray-400 text-sm">Telegram Clicks</p>
                <p className="text-xl font-bold text-white">{formatNumber(totalTelegramClicks)}</p>
              </div>
            </div>
            <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
              <div 
                className="h-full bg-sky-500 rounded-full"
                style={{ width: `${totalClicks ? (totalTelegramClicks / totalClicks) * 100 : 0}%` }}
              />
            </div>
            <p className="text-gray-500 text-xs mt-2">
              {totalClicks ? ((totalTelegramClicks / totalClicks) * 100).toFixed(1) : 0}% of total clicks
            </p>
          </div>
        </div>

        {/* Ads Performance Table */}
        <div className="bg-slate-900/50 border border-white/10 rounded-xl overflow-hidden">
          <div className="p-6 border-b border-white/10">
            <h2 className="text-xl font-semibold text-white">Ad Performance</h2>
            <p className="text-gray-400 text-sm mt-1">Detailed metrics for each of your ads</p>
          </div>

          {ads.length === 0 ? (
            <div className="p-12 text-center">
              <BarChart3 className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">No ads posted yet</p>
              <p className="text-gray-500 text-sm mt-1">Post your first ad to see analytics</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-800/50">
                  <tr>
                    <th className="text-left text-gray-400 font-medium text-sm px-6 py-4">Ad Title</th>
                    <th className="text-center text-gray-400 font-medium text-sm px-4 py-4">Status</th>
                    <th className="text-center text-gray-400 font-medium text-sm px-4 py-4">Views</th>
                    <th className="text-center text-gray-400 font-medium text-sm px-4 py-4">WhatsApp</th>
                    <th className="text-center text-gray-400 font-medium text-sm px-4 py-4">Calls</th>
                    <th className="text-center text-gray-400 font-medium text-sm px-4 py-4">Telegram</th>
                    <th className="text-center text-gray-400 font-medium text-sm px-4 py-4">CTR</th>
                    <th className="text-center text-gray-400 font-medium text-sm px-4 py-4">Bumps</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {ads.map((ad) => {
                    const adClicks = (ad.whatsapp_clicks || 0) + (ad.call_clicks || 0) + (ad.telegram_clicks || 0);
                    const isExpired = ad.expires_at && new Date(ad.expires_at) < new Date();
                    
                    return (
                      <tr key={ad.id} className="hover:bg-slate-800/30 transition-colors">
                        <td className="px-6 py-4">
                          <div className="max-w-xs">
                            <p className="text-white font-medium truncate">{ad.title}</p>
                            <p className="text-gray-500 text-xs mt-1">
                              Posted {new Date(ad.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            isExpired ? "bg-gray-500/20 text-gray-400" :
                            ad.status === "approved" ? "bg-emerald-500/20 text-emerald-400" :
                            ad.status === "pending" ? "bg-amber-500/20 text-amber-400" :
                            "bg-red-500/20 text-red-400"
                          }`}>
                            {isExpired ? "Expired" : ad.status}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <span className="text-white font-medium">{formatNumber(ad.views || 0)}</span>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <span className="text-emerald-400">{ad.whatsapp_clicks || 0}</span>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <span className="text-blue-400">{ad.call_clicks || 0}</span>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <span className="text-sky-400">{ad.telegram_clicks || 0}</span>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <span className="text-amber-400">{calculateCTR(adClicks, ad.views)}</span>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <span className="text-purple-400">{ad.top_up_count || 0}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Tips Section */}
        <div className="mt-8 bg-gradient-to-r from-rose-900/20 to-amber-900/20 border border-white/10 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Tips to Improve Performance</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-rose-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <TrendingUp className="w-4 h-4 text-rose-400" />
              </div>
              <div>
                <p className="text-white text-sm font-medium">Bump your ads regularly</p>
                <p className="text-gray-400 text-xs">Bumped ads appear at the top of listings</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-amber-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <Clock className="w-4 h-4 text-amber-400" />
              </div>
              <div>
                <p className="text-white text-sm font-medium">Choose optimal time slots</p>
                <p className="text-gray-400 text-xs">Evening slots (6PM-12AM) typically get more views</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-emerald-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <Users className="w-4 h-4 text-emerald-400" />
              </div>
              <div>
                <p className="text-white text-sm font-medium">Post in multiple cities</p>
                <p className="text-gray-400 text-xs">Expand your reach to maximize visibility</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <Eye className="w-4 h-4 text-blue-400" />
              </div>
              <div>
                <p className="text-white text-sm font-medium">Use high-quality images</p>
                <p className="text-gray-400 text-xs">Attractive photos increase click-through rates</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Analytics;
